'use client';

import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

interface AnimationPreferencesContextValue {
  animationsEnabled: boolean;
  setAnimationsEnabled: (enabled: boolean) => void;
  toggleAnimations: () => void;
}

const AnimationPreferencesContext = createContext<AnimationPreferencesContextValue | null>(null);

const STORAGE_KEY = 'qm-animations-enabled';

export function AnimationPreferencesProvider({ children }: { children: ReactNode }) {
  // Default to true, will be overwritten by localStorage if available
  const [animationsEnabled, setAnimationsEnabledState] = useState(true);
  const [isHydrated, setIsHydrated] = useState(false);

  // Load preference from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored !== null) {
      setAnimationsEnabledState(stored === 'true');
    }
    setIsHydrated(true);
  }, []);

  const setAnimationsEnabled = (enabled: boolean) => {
    setAnimationsEnabledState(enabled);
    localStorage.setItem(STORAGE_KEY, String(enabled));
  };

  const toggleAnimations = () => {
    setAnimationsEnabled(!animationsEnabled);
  };

  // Don't render children until hydrated to avoid flash
  // Actually, we should render but just use default value
  return (
    <AnimationPreferencesContext.Provider
      value={{
        // Use default (true) until hydrated to avoid hydration mismatch
        animationsEnabled: isHydrated ? animationsEnabled : true,
        setAnimationsEnabled,
        toggleAnimations,
      }}
    >
      {children}
    </AnimationPreferencesContext.Provider>
  );
}

export function useAnimationPreferences() {
  const context = useContext(AnimationPreferencesContext);
  if (!context) {
    // Return default values if used outside provider
    return {
      animationsEnabled: true,
      setAnimationsEnabled: () => {},
      toggleAnimations: () => {},
    };
  }
  return context;
}
